import { useState } from 'react';
import './leaders-view.styles.scss';
import { Link } from 'react-router-dom';
import { TEAM_CATEGORIES, formatTeamValue, rankTeams, withRecords } from '@shared/teamStats.js';
import { recordText } from '@/utils/leagueLabels';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamStatsTable from './team-stats-table.component';

const SHOWN = 25;
const fitText = (v) => (v === undefined ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(3)}`);

// Team stat rankings. teamStats: summary/teamStats; teams: summary/teams map.
// chemistry: {teamId: number}, the hidden roster fit minus neutral. Admin
// tools only; never pass it on a manager screen.
const TeamLeaders = ({ teamStats, teams, teamLink, highlightId, chemistry }) => {

    const [ category, setCategory ] = useState(TEAM_CATEGORIES[0].key);
    const [ scope, setScope ] = useState('teams');
    const [ showAll, setShowAll ] = useState(false);
    const all = category === 'all';
    const cat = TEAM_CATEGORIES.find(c => c.key === category) ?? TEAM_CATEGORIES[0];
    const hasPlayoffs = Object.keys(teamStats?.playoffs ?? {}).length > 0;
    const playoffs = hasPlayoffs && scope === 'playoffs';
    const stats = withRecords(teamStats?.[hasPlayoffs ? scope : 'teams'], teams, playoffs);
    const ranked = rankTeams(cat, stats).filter(r => teams?.[r.teamId]);
    // Your own team stays in view even outside the top 25.
    const rows = showAll ? ranked : ranked.filter((r, i) => i < SHOWN || r.teamId === highlightId);

    return (
        <>
            {hasPlayoffs &&
                <div className='category-chips' role='tablist' aria-label='Games'>
                    {[['teams', 'Regular season'], ['playoffs', 'Playoffs']].map(([key, label]) => (
                        <button key={key} role='tab' aria-selected={scope === key}
                            className={`chip ${scope === key ? 'active' : ''}`}
                            onClick={() => setScope(key)}>
                            {label}
                        </button>
                    ))}
                </div>
            }
            <div className='category-chips' role='tablist' aria-label='Stat'>
                <button role='tab' aria-selected={all} className={`chip ${all ? 'active' : ''}`} onClick={() => setCategory('all')}>
                    All stats
                </button>
                {TEAM_CATEGORIES.map(c => (
                    <button
                        key={c.key}
                        role='tab'
                        aria-selected={c.key === category}
                        className={`chip ${c.key === category ? 'active' : ''}`}
                        onClick={() => setCategory(c.key)}>
                        {c.label}
                    </button>
                ))}
            </div>
            {all ?
                <TeamStatsTable stats={stats} teams={teams} teamLink={teamLink} highlightId={highlightId} playoffs={playoffs} /> :
            ranked.length === 0 ?
                <p className='meta'>Team stats appear once games have been played.</p> :
                <div className='g-card'>
                    {cat.low && <p className='meta'>Lower is better.</p>}
                    <div className='table-scroll'>
                        <table className='g-table leaders-table'>
                            <thead>
                                <tr>
                                    <th className='num'>#</th>
                                    <th>Team</th>
                                    <th className='num'>W-L</th>
                                    <th className='num'>{cat.short}</th>
                                    <th className='num' title='Chemistry grade'>Chem</th>
                                    {chemistry && <th className='num hidden-col' title='Hidden roster fit minus neutral (admin only)'>Fit</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const team = teams[r.teamId];
                                    const record = playoffs ?
                                        `${team.record?.postW ?? 0}-${team.record?.postL ?? 0}` :
                                        recordText(team.record);
                                    // Your team, added below the top 25.
                                    const gap = !showAll && i === SHOWN;
                                    return (
                                        <tr key={r.teamId} className={`${r.teamId === highlightId ? 'highlight' : ''} ${gap ? 'gap' : ''}`}>
                                            <td className='num'>{r.rank}</td>
                                            <td>
                                                <Link to={teamLink(r.teamId)} className='team-link'>
                                                    <TeamBadge team={team} size='sm' />
                                                    <span className='team-name'>{team.name}</span>
                                                </Link>
                                            </td>
                                            <td className='num'>{record}</td>
                                            <td className='num value'>{formatTeamValue(cat, r.value)}</td>
                                            <td className='num'>{team.chemistryGrade?.grade ?? '-'}</td>
                                            {chemistry && <td className='num hidden-col'>{fitText(chemistry[r.teamId])}</td>}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    {ranked.length > SHOWN &&
                        <button className='g-button text-only show-all' onClick={() => setShowAll(!showAll)}>
                            {showAll ? `Show top ${SHOWN}` : `Show all ${ranked.length} teams`}
                        </button>
                    }
                </div>
            }
        </>
    )
}

export default TeamLeaders
